import * as Tabs from '@radix-ui/react-tabs'
import { ReactNode } from 'react'

import { SizeOptions } from '../../types/types'
import { AstraParagraph } from '../Typography/AstraParagraph/AstraParagraph'
import { astraTwMerge } from '../Utilities/astraTwMerge'

const gapStyles: Record<SizeOptions, string> = {
    small: 'gap-1',
    medium: 'gap-2',
    large: 'gap-2',
}

export type TabOption<TabValue> = {
    label?: string
    value: TabValue
    content?: ReactNode
}

const sizeStyles: Record<SizeOptions, string> = {
    small: 'h-5 px-[6px] rounded',
    medium: 'h-7 px-2 rounded-md',
    large: 'h-9 px-2 rounded-lg',
}

type Props<TabValue> = {
    options: Readonly<TabOption<TabValue>[]>
    fullHeight?: boolean
    noPadding?: boolean
    noSeparatorLine?: boolean
    size?: SizeOptions
    highContrast?: boolean

    // uncontrolled input
    defaultValue?: TabValue

    // controlled input
    value?: TabValue
    onValueChange?: (value: TabValue) => void
}

export const AstraTabs = <TabValue extends string>({
    options,
    value,
    onValueChange,
    defaultValue,
    fullHeight,
    noPadding = false,
    noSeparatorLine = false,
    size = 'medium',
    highContrast = false,
}: Props<TabValue>) => {
    if (defaultValue && value) {
        console.error('AstraTabs: Cannot provide both `defaultValue` and `value` props.')
    }

    const isTabValue = (value: string): value is TabValue => options.some(option => option.value === value)

    const handleValueChange = (nextValue: string) => {
        if (isTabValue(nextValue)) {
            onValueChange?.(nextValue)
        }
    }

    const getTextVariant = () => (size === 'small' ? 'body-small' : 'body')

    return (
        <Tabs.Root
            defaultValue={defaultValue}
            value={value}
            onValueChange={handleValueChange}
            className={astraTwMerge(fullHeight ? 'flex h-full flex-col overflow-auto' : '', !noSeparatorLine ? 'divide-y divide-base' : '')}
        >
            <Tabs.List className={astraTwMerge('flex p-4', gapStyles[size], noPadding ? 'p-0' : '')}>
                {options.map(item => (
                    <Tabs.Trigger
                        key={item.value}
                        value={item.value}
                        data-test={`tab-${item.value}`}
                        className={astraTwMerge(
                            sizeStyles[size],
                            'flex items-center justify-center text-muted transition-all',
                            highContrast
                                ? 'bg-surface-base hover:border-hover hover:bg-neutral-subtle-hover data-[state="active"]:bg-brand-strong-rest data-[state="active"]:text-inverted'
                                : 'hover:bg-surface-muted data-[state="active"]:bg-surface-strong data-[state="active"]:text-default'
                        )}
                    >
                        <AstraParagraph weight="medium" variant={getTextVariant()} className="text-nowrap text-inherit">
                            {item.label ?? item.value}
                        </AstraParagraph>
                    </Tabs.Trigger>
                ))}
            </Tabs.List>
            {options.map(item => (
                <Tabs.Content key={item.value} value={item.value} asChild>
                    {item.content}
                </Tabs.Content>
            ))}
        </Tabs.Root>
    )
}
